import { useNavigate } from 'react-router-dom';
import {
    BookOpen,
    CheckCircle2,
    Clock,
    Calendar,
    ChevronRight,
    Play,
    XCircle,
    Trophy,
    Target,
    Circle,
} from 'lucide-react';
import type { ErrorBooklet } from '../../../../types/error-booklet';
import { ApiService } from '../../../../services/api';
import DynamicList from '../../../../components/dynamic/List';

interface ErrorBookletListProps {
    onSelectBooklet?: (bookletId: string) => void;
}

const monthNames = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
];

export default function ErrorBookletList({ onSelectBooklet }: ErrorBookletListProps) {
    const navigate = useNavigate();

    const handleBookletClick = (bookletId: string) => {
        if (onSelectBooklet) {
            onSelectBooklet(bookletId);
        } else {
            navigate(`/lessons?tab=error-booklet&bookletId=${bookletId}`);
        }
    };

    const getStatusInfo = (status: string) => {
        switch (status) {
            case 'COMPLETED':
                return {
                    label: 'Tamamlandı',
                    badgeClass: 'bg-green-50 text-green-700 border-green-200',
                    icon: CheckCircle2,
                };
            case 'IN_PROGRESS':
                return {
                    label: 'Devam Ediyor',
                    badgeClass: 'bg-blue-50 text-blue-700 border-blue-200',
                    icon: Play,
                };
            default:
                return {
                    label: 'Bekliyor',
                    badgeClass: 'bg-amber-50 text-amber-700 border-amber-200',
                    icon: Clock,
                };
        }
    };

    const calculateScore = (booklet: ErrorBooklet) => {
        if (booklet.totalQuestions === 0) return 0;
        return Math.round((booklet.correctAnswers / booklet.totalQuestions) * 100);
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return null;
        return new Date(dateString).toLocaleDateString('tr-TR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
    };

    const formatDateTime = (dateString: string | null) => {
        if (!dateString) return null;
        return new Date(dateString).toLocaleDateString('tr-TR', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getGradientByBookletId = (bookletId: string) => {
        const numericId = bookletId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        const gradientIndex = numericId % 20;

        const gradients = [
            'from-blue-600 via-blue-500 to-indigo-500',
            'from-emerald-600 via-teal-500 to-cyan-500',
            'from-purple-600 via-violet-500 to-indigo-500',
            'from-teal-600 via-emerald-500 to-green-500',
            'from-indigo-600 via-blue-500 to-sky-500',
            'from-cyan-600 via-teal-500 to-emerald-500',
            'from-violet-600 via-purple-500 to-pink-500',
            'from-sky-600 via-blue-500 to-indigo-500',
            'from-green-600 via-emerald-500 to-teal-500',
            'from-slate-600 via-blue-500 to-indigo-500',
            'from-rose-500 via-pink-500 to-purple-500',
            'from-orange-500 via-amber-500 to-yellow-500',
            'from-emerald-500 via-green-500 to-teal-500',
            'from-blue-500 via-indigo-500 to-purple-500',
            'from-teal-500 via-cyan-500 to-sky-500',
            'from-purple-500 via-indigo-500 to-blue-500',
            'from-amber-500 via-orange-500 to-red-500',
            'from-green-500 via-teal-500 to-cyan-500',
            'from-indigo-500 via-purple-500 to-violet-500',
            'from-slate-500 via-gray-500 to-zinc-500',
        ];

        return gradients[gradientIndex];
    };

    const renderBookletItem = (booklet: ErrorBooklet, viewMode?: string) => {
        const statusInfo = getStatusInfo(booklet.status);
        const StatusIcon = statusInfo.icon;
        const score = calculateScore(booklet);
        const isCurrentMonth = booklet.month === new Date().getMonth() + 1 && booklet.year === new Date().getFullYear();
        const wrongAnswers = booklet.solvedQuestions - booklet.correctAnswers;
        const unanswered = booklet.totalQuestions - booklet.solvedQuestions;

        if (viewMode === 'grid') {
            return (
                <div
                    key={booklet.id}
                    onClick={() => handleBookletClick(booklet.id)}
                    className="bg-white rounded-xl shadow-md border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden group mb-6 flex flex-col h-full cursor-pointer"
                >
                    <div className={`relative h-32 bg-gradient-to-br ${getGradientByBookletId(booklet.id)} overflow-hidden`}>
                        <div className="w-full h-full flex items-center justify-center">
                            <Calendar className="w-12 h-12 text-white opacity-90" />
                        </div>
                        {isCurrentMonth && (
                            <div className="absolute top-2 right-2 bg-white/20 backdrop-blur-sm text-white text-xs font-medium px-2 py-1 rounded">
                                Bu Ay
                            </div>
                        )}
                    </div>

                    <div className="p-4 flex flex-col flex-1">
                        <div className="mb-3">
                            <h3 className="font-bold text-lg text-gray-900 mb-1 group-hover:text-purple-600 transition-colors">
                                {monthNames[booklet.month - 1]} {booklet.year}
                            </h3>
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${statusInfo.badgeClass}`}>
                                    <StatusIcon className="w-3 h-3" />
                                    {statusInfo.label}
                                </span>
                                {booklet.status === 'COMPLETED' && (
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${
                                        score >= 80 ? 'bg-green-100 text-green-700' : score >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                                    }`}>
                                        <Trophy className="w-3 h-3" />
                                        %{score}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                            <div className="flex items-center gap-1.5 text-gray-600">
                                <Target className="w-4 h-4 text-gray-400" />
                                <span>{booklet.totalQuestions} Soru</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-gray-600">
                                <BookOpen className="w-4 h-4 text-gray-400" />
                                <span>{booklet.solvedQuestions} Çözüldü</span>
                            </div>
                            {booklet.status === 'COMPLETED' && (
                                <>
                                    <div className="flex items-center gap-1.5 text-green-600">
                                        <CheckCircle2 className="w-4 h-4" />
                                        <span>{booklet.correctAnswers} Doğru</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-red-600">
                                        <XCircle className="w-4 h-4" />
                                        <span>{wrongAnswers} Yanlış</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-amber-600">
                                        <Circle className="w-4 h-4" />
                                        <span>{unanswered} Boş</span>
                                    </div>
                                    {booklet.completedAt && (
                                        <div className="text-gray-600">
                                            <div className="flex items-center gap-1.5">
                                                <Clock className="w-4 h-4 text-gray-400" />
                                                <span>Tamamlanma:</span>
                                            </div>
                                            <div className="pl-6 text-sm">{formatDateTime(booklet.completedAt)}</div>
                                        </div>
                                    )}
                                </>
                            )}
                            {booklet.status === 'PENDING' && booklet.createdAt && (
                                <div className="text-gray-600 col-span-2">
                                    <div className="flex items-center gap-1.5">
                                        <Calendar className="w-4 h-4 text-gray-400" />
                                        <span>Oluşturulma:</span>
                                    </div>
                                    <div className="pl-6 text-sm">{formatDate(booklet.createdAt)}</div>
                                </div>
                            )}
                            {booklet.status === 'IN_PROGRESS' && booklet.startedAt && (
                                <div className="text-gray-600 col-span-2">
                                    <div className="flex items-center gap-1.5">
                                        <Play className="w-4 h-4 text-gray-400" />
                                        <span>Başlama:</span>
                                    </div>
                                    <div className="pl-6 text-sm">{formatDateTime(booklet.startedAt)}</div>
                                </div>
                            )}
                        </div>

                        <div className="flex gap-3 items-center mt-4">
                            <button
                                className="flex-1 inline-flex items-center justify-center gap-2 bg-purple-600 text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-purple-700 shadow-lg transition-colors"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleBookletClick(booklet.id);
                                }}
                            >
                                {booklet.status === 'COMPLETED' ? 'Sonuçları Gör' : booklet.status === 'IN_PROGRESS' ? 'Devam Et' : 'Teste Başla'}
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div
                key={booklet.id}
                onClick={() => handleBookletClick(booklet.id)}
                className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow mb-2 cursor-pointer"
            >
                <div className="flex flex-col lg:grid lg:grid-cols-12 gap-3 lg:gap-3 lg:items-center">
                    <div className="lg:col-span-3 flex items-start gap-3 min-w-0">
                        <div className={`relative w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br ${getGradientByBookletId(booklet.id)} rounded-lg overflow-hidden flex-shrink-0`}>
                            <div className="w-full h-full flex items-center justify-center">
                                <Calendar className="w-6 h-6 sm:w-7 sm:h-7 text-white opacity-80" />
                            </div>
                        </div>

                        <div className="flex-1 min-w-0">
                            <h3 className="text-base sm:text-lg font-semibold text-gray-900 hover:text-purple-600 transition-colors break-words mb-1">
                                {monthNames[booklet.month - 1]} {booklet.year}
                                {isCurrentMonth && (
                                    <span className="ml-2 text-xs font-medium text-purple-600 bg-purple-50 px-2 py-0.5 rounded">Bu Ay</span>
                                )}
                            </h3>
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${statusInfo.badgeClass}`}>
                                    <StatusIcon className="w-3 h-3" />
                                    {statusInfo.label}
                                </span>
                                {booklet.status === 'COMPLETED' && (
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${
                                        score >= 80 ? 'bg-green-100 text-green-700' : score >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                                    }`}>
                                        <Trophy className="w-3 h-3" />
                                        %{score}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-6 flex flex-wrap items-center gap-3 min-w-0">
                        <div className="flex items-center gap-4 text-sm">
                            <span className="flex items-center gap-1.5 text-gray-600">
                                <Target className="w-4 h-4 text-gray-400" />
                                {booklet.totalQuestions} Soru
                            </span>
                            <span className="flex items-center gap-1.5 text-gray-600">
                                <BookOpen className="w-4 h-4 text-gray-400" />
                                {booklet.solvedQuestions} Çözüldü
                            </span>
                            {booklet.status === 'COMPLETED' && (
                                <>
                                    <span className="flex items-center gap-1.5 text-green-600">
                                        <CheckCircle2 className="w-4 h-4" />
                                        {booklet.correctAnswers} Doğru
                                    </span>
                                    <span className="flex items-center gap-1.5 text-red-600">
                                        <XCircle className="w-4 h-4" />
                                        {wrongAnswers} Yanlış
                                    </span>
                                    <span className="flex items-center gap-1.5 text-amber-600">
                                        <Circle className="w-4 h-4" />
                                        {unanswered} Boş
                                    </span>
                                </>
                            )}
                        </div>

                        {booklet.status === 'COMPLETED' && booklet.completedAt && (
                            <span className="flex items-center gap-1.5 text-sm text-gray-600">
                                <Clock className="w-4 h-4 text-gray-400" />
                                Tamamlanma: {formatDateTime(booklet.completedAt)}
                            </span>
                        )}
                        {booklet.status === 'PENDING' && booklet.createdAt && (
                            <span className="flex items-center gap-1.5 text-sm text-gray-600">
                                <Calendar className="w-4 h-4 text-gray-400" />
                                Oluşturulma: {formatDate(booklet.createdAt)}
                            </span>
                        )}
                        {booklet.status === 'IN_PROGRESS' && booklet.startedAt && (
                            <span className="flex items-center gap-1.5 text-sm text-gray-600">
                                <Play className="w-4 h-4 text-gray-400" />
                                Başlama: {formatDateTime(booklet.startedAt)}
                            </span>
                        )}
                    </div>

                    <div className="lg:col-span-3 flex items-center justify-start lg:justify-end gap-2 flex-wrap flex-shrink-0">
                        <button
                            className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 transition-colors whitespace-nowrap"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleBookletClick(booklet.id);
                            }}
                        >
                            {booklet.status === 'COMPLETED' ? 'Sonuçları Gör' : booklet.status === 'IN_PROGRESS' ? 'Devam Et' : 'Teste Başla'}
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const fetcher = async (page: number, searchTerm?: string) => {
        try {
            const response = await ApiService.getMyErrorBooklets({ page, limit: 10 });
            return {
                data: response.data || [],
                meta: {
                    page: response.pagination?.page || page,
                    totalPages: response.pagination?.totalPages || 1,
                    totalItems: response.pagination?.total || 0,
                    limit: response.pagination?.limit || 10,
                },
            };
        } catch (error: any) {
            console.error('Error loading booklets:', error);
            throw error;
        }
    };

    return (
        <DynamicList
            type="list"
            header={false}
            title="Hata Kitapçıkları"
            path="error-booklet/my"
            fetcher={fetcher}
            isSearch={false}
            isPagination={true}
            showToggle={true}
            emptyMessage="Henüz hata kitapçığı bulunmuyor. Testlerde yanlış cevapladığın sorular olduğunda burada listelenecek."
            errorMessage="Hata kitapçıkları yüklenirken bir sorun oluştu."
            item={renderBookletItem}
        />
    );
}